/**
 * The organization's agents as one shared fetch, read by the agent panels, the cameras list,
 * the rule editors and the camera settings.
 *
 * The stream sends no frame for an agent, so the list and each agent's settings load when
 * their first reader mounts and when the cloud answers again after a failure.
 */

import { atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import type { AgentSettingsSnapshot, AgentsSnapshot } from '@/types/agents';
import { PortalEvent } from '@/types/portal';
import { SnapshotCommand, type SnapshotWrite } from '@/types/snapshot';

const PENDING: AgentsSnapshot = {
    agents: [],
    isLoaded: false,
    isLoading: true,
    error: null,
};

const agents = snapshotAtoms<AgentsSnapshot>(PENDING, async (): Promise<AgentsSnapshot> => ({
    agents: await api.listAgents(),
    isLoaded: true,
    isLoading: false,
    error: null,
}));

export const agentsAtom = agents.value;

const NO_SETTINGS: AgentSettingsSnapshot = {
    settings: null,
    isLoading: true,
    error: null,
};

/** One agent's settings: read it for the snapshot, set it with a command. */
export const agentSettingsAtom = atomFamily((agentId: string) => snapshotMemberAtom(
    snapshotAtoms<AgentSettingsSnapshot>(NO_SETTINGS, async (): Promise<
        AgentSettingsSnapshot
    > => ({
        settings: await api.getAgentSettings(agentId),
        isLoading: false,
        error: null,
    })),
));

const NO_AGENT: AgentSettingsSnapshot = {
    settings: null,
    isLoading: false,
    error: null,
};

/** What the panel reads while it has no agent: there is nothing to load. */
export const noAgentSettingsAtom: WritableAtom<
    AgentSettingsSnapshot, [SnapshotWrite<AgentSettingsSnapshot>], void
> = atom(
    (): AgentSettingsSnapshot => NO_AGENT,
    (_get, _set, _write: SnapshotWrite<AgentSettingsSnapshot>): void => undefined,
);

const RELOAD = { command: SnapshotCommand.Reload } as const;

/** A load that failed while the cloud was away stays failed until a reload. */
export const agentsPortalAtom = atom(null, (_get, set, event: PortalEvent): void => {
    if (event !== PortalEvent.Answered) {
        return;
    }
    set(agents.reload);
    // A member that nothing reads only goes stale: its next reader loads it.
    for (const agentId of agentSettingsAtom.getParams()) {
        set(agentSettingsAtom(agentId), RELOAD);
    }
});
